import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'react-apollo'
import { Container } from '@scentregroup/ui'
import classNames from 'classnames'
import pagesHoc from '../pages-hoc'
import withProducts from '../../containers/with-products'
import withProductCategories from '../../containers/with-product-categories'
import containerStyle from '../../components/container.scss'
import canonicalLink from '../../helpers/canonical-link'
import Filter from './filter'
import style from './index.scss'
import ProductGallery from './products-gallery'
import ProductSearchBar from './product-search-bar'
import CategoryDescription from './category-description'
import ProductTitle from './product-title'
import NoProducts from './no-products'

/**
 * Updates subcategories for this list of products against category information
 * to provide more information for the filter component.
 * @param {string} currentCategory The current category path
 * @param {*} categoryPaths A list of categoryPaths that represents the
 * category paths facet for this product search
 * @param {*} subcategories The definitive list of subcategories from the
 * categories service for this particular category path
 */
function getRelevantCategories(
  currentCategory,
  categoryPaths = [],
  subcategories
) {
  const categoryMap = {}
  subcategories.forEach(category => (categoryMap[category.path] = category))

  const mapCategory = path => ({
    path,
    value: categoryMap[path].name,
    description: categoryMap[path].description,
    image: categoryMap[path].image,
  })

  return {
    currentCategory: mapCategory(currentCategory),
    categories: categoryPaths
      .filter(path => categoryMap[path])
      .map(mapCategory),
  }
}

/**
 * Groups the given categories by their root parent, i.e. products/mens-fashion
 * @param {object} relevantCatgories The relevant categories for this products list
 */
function groupCategories(relevantCatgories) {
  const rootCategoryRegEx = RegExp('[^/]+/[^/]+')
  return relevantCatgories.reduce((categoriesMap, category) => {
    const rootCategory = rootCategoryRegEx.exec(category.path)
    const categoryList = categoriesMap[rootCategory] || []
    categoryList.push(category)
    categoriesMap[rootCategory] = categoryList
    return categoriesMap
  }, {})
}

export class ProductsPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      filterOpen: false,
    }
  }

  toggleFilter = () => {
    this.setState({ filterOpen: !this.state.filterOpen })
  }

  render() {
    const {
      products,
      pagination,
      buildUrl,
      filterState,
      facets,
      productCategories,
      history,
      categoriesHierarchy,
      hideCategoryInformation,
    } = this.props

    const buildUrlWithPageReset = update =>
      buildUrl(
        Object.assign({}, filterState, Object.assign({ page: 0 }, update))
      )

    let updatedFacets = {}
    if (facets) {
      const relevantCatgories = getRelevantCategories(
        filterState.categoryPath,
        facets.categoryPaths,
        productCategories
      )

      Object.assign(filterState, {
        currentCategory: relevantCatgories.currentCategory,
      })

      updatedFacets = Object.assign({}, facets, {
        categories: groupCategories(relevantCatgories.categories),
      })
    }

    if (products.length === 0) {
      return (
        <Container type="large" className={containerStyle.container}>
          <div className={style.productsWrap}>
            {!hideCategoryInformation && (
              <ProductTitle
                title={categoriesHierarchy[0].name}
                className={style.pageTitle}
                categoriesHierarchy={categoriesHierarchy}
              />
            )}
            <NoProducts />
          </div>
        </Container>
      )
    }

    return (
      <React.Fragment>
        <Container type="large" className={containerStyle.container}>
          <div className={style.productsWrap}>
            {!hideCategoryInformation && (
              <ProductTitle
                title={categoriesHierarchy[0].name}
                className={style.pageTitle}
                categoriesHierarchy={categoriesHierarchy}
              />
            )}
            <div
              className={classNames(style.productsBody, {
                [style.filterOpen]: this.state.filterOpen,
              })}
            >
              <nav className={style.productsFilter}>
                <Filter
                  filterState={filterState}
                  facets={updatedFacets}
                  buildUrl={update => buildUrlWithPageReset(update)}
                  history={history}
                  categoriesHierarchy={categoriesHierarchy}
                />
              </nav>
              <div className={style.galleryWrap}>
                <ProductSearchBar
                  filterState={filterState}
                  buildUrl={buildUrlWithPageReset}
                  history={history}
                  filterClick={this.toggleFilter}
                  mobileFilterOpen={this.state.filterOpen}
                  showSaleFilter={facets.saleItems}
                />
                <ProductGallery
                  products={products}
                  {...pagination}
                  filterState={filterState}
                  facets={updatedFacets}
                  buildUrl={update => buildUrlWithPageReset(update)}
                  className={style.ProductGallery}
                />
              </div>
            </div>
          </div>
        </Container>
        {!hideCategoryInformation && (
          <CategoryDescription
            name={filterState.currentCategory.value}
            {...filterState.currentCategory}
          />
        )}
      </React.Fragment>
    )
  }
}

ProductsPage.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.object,
  filterState: PropTypes.object,
  buildUrl: PropTypes.func.isRequired,
  facets: PropTypes.object,
  history: PropTypes.object,
  productCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  categoriesHierarchy: PropTypes.arrayOf(PropTypes.object),
  hideCategoryInformation: PropTypes.bool,
}

const storeName = (store, facets) => {
  if (facets.retailers) {
    const retailer = facets.retailers.find(x => x.slug === store)
    return (retailer && retailer.name) || ''
  }
  return ''
}

const getMetaData = ({
  categoriesHierarchy,
  pagination,
  filterState,
  buildUrl,
  facets,
}) => {
  let description = ''
  let title = false
  const categoryName = categoriesHierarchy[0].name
  const total = pagination && pagination.totalItems

  if (
    filterState.store ||
    filterState.color ||
    filterState.brand ||
    filterState.on_sale
  ) {
    const brand = filterState.brand ? filterState.brand : ''
    const colorTitle = filterState.color ? filterState.color : ''
    const storeTitle = filterState.store
      ? `from ${storeName(filterState.store, facets)}`
      : ''
    const saleTitle = filterState.on_sale
      ? `| Shop ${categoryName} on Sale`
      : ''
    const colorDescription = filterState.color ? `in ${filterState.color}` : ''
    const saleDescription = filterState.on_sale ? `on sale` : ''
    title = `${brand} ${colorTitle} ${categoryName} ${storeTitle} ${saleTitle} | Westfield`
      .replace(/\s+/g, ' ')
      .trim()
    description = `Shop the best of ${categoryName} products ${colorDescription} ${saleDescription} ${storeTitle} at Westfield. Browse from over ${total} ${categoryName} products ${colorDescription} ${storeTitle} and buy from a Westfield retailer.`
      .replace(/\s+/g, ' ')
      .trim()
  } else {
    title = `${categoryName} | Shop ${categoryName} at Westfield`
    description = `Browse from over ${total} ${categoryName} products. Find the latest ${categoryName} from top brands and buy from a Westfield retailer.`
  }

  return { title, description }
}

// Note the order of the functions provided to compose. withProducts must be the
// first function as per
// https://www.apollographql.com/docs/react/api/react-apollo.html#compose as the
// code depends on the facets and pagination objects being present.
export default compose(
  withProductCategories,
  withProducts
)(pagesHoc(ProductsPage, getMetaData, canonicalLink.products))
